import * as Sentry from '@sentry/browser';
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';

import makeApiRequest from '../../utils/makeApiRequest';

import { API_ENDPOINTS } from '../../constants';
import Button from './Button';
import Header from './Header';

const ERRORS_ACCOUNT = ['8A'];
const ERRORS_PIN = ['8C', '6B'];

const StepNumberTransferIssue = ({ onTransferStarted, onTransferSubmitted, customerId, transferError }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState(false);
  const [errors, setErrors] = useState({});

  const [phoneNumber] = useState(transferError?.MDN);
  const [accountNumber, setAccountNumber] = useState(transferError?.OspAccountNo);
  const [transferPin, setTransferPin] = useState(transferError?.OspAccountPass);
  const [zipCode, setZipCode] = useState(transferError?.MarketZipCode);

  useEffect(() => {
    if (ERRORS_ACCOUNT.includes(transferError?.PortInCode)) {
      setAccountNumber('');
      setErrors({ ospAccountNumber: 'Enter a valid account number.' });
    }

    if (ERRORS_PIN.includes(transferError?.PortInCode)) {
      setTransferPin('');
      setErrors({ ospAccountPassword: 'Enter a valid Transfer PIN.' });
    }
  }, [transferError]);

  const handleErrors = (errors) => {
    console.log(errors);
    if (errors?.portIn === 'Failed') {
      if (errors.error.error === 'INELIGIBLE_MSISDN') {
        setErrors({ portInMdn: "The phone number isn't eligible to transfer." });
      } else {
        setErrors({});
      }
    } else if (errors?.error === 'INVALID_ZIP') {
      setErrors({ zipCode: 'The ZIP code is not correct.' });
    } else {
      setErrors(errors);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setShowError(false);

    const formData = new FormData(e.target);
    const formatedData = {
      CustomerId: customerId,
      zipCode: formData.get('zip'),
      portInMdn: transferError?.MDN,
      ospAccountNumber: formData.get('accountNumber'),
      ospAccountPassword: formData.get('transferPin'),
    };

    const endpoint =
      transferError.PortInStatus === 'ResolutionRequired'
        ? API_ENDPOINTS.ACTIVATION_TRANSFER_UPDATE
        : API_ENDPOINTS.ACTIVATION_TRANSFER;

    makeApiRequest(endpoint, formatedData, 'POST', true)
      .then((data) => {
        console.log(data);
        if (data.Status === true && data.nextStep) {
          setIsLoading(false);
          onTransferStarted(data.nextStep);
        } else if (data.Status === true && data.data.portIn === 'Submitted') {
          setIsLoading(false);
          onTransferSubmitted();
        } else {
          handleErrors(data.error);
          setShowError(true);
          setIsLoading(false);
          throw new Error(`Failed to update transfer | ${JSON.stringify(data.error)}`);
        }
      })
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
        setShowError(true);
        setIsLoading(false);
      });
  };

  const handleReset = (e) => {
    e.preventDefault();
    setResetting(true);
    setResetError(false);

    makeApiRequest(API_ENDPOINTS.ACTIVATION_RESET, { CustomerId: customerId })
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        console.error(error);
        setResetting(false);
        setResetError(true);
        Sentry.captureException(error);
      });
  };

  let errorMessage;

  if (transferError?.PortInStatusDes) {
    errorMessage = `We couldn't transfer your number due to the following reason: ${transferError?.PortInStatusDes}`;
  } else {
    errorMessage = `We couldn't transfer your number. Please check the details and try again.`;
  }

  return html`
    <${Header} 
      title="Update Your Transfer" 
      description=${errorMessage}
      eyebrow="Oops! Something went wrong!" 
      isError 
    />
    <div class="flex flex-col w-full lg:w-1/2">
      <form onSubmit=${handleSubmit} class="flex flex-col gap-3 items-stretch w-full">
        <fieldset class="flex flex-col">
          <label for="phoneNumber" class="text-xs mb-1">Current Phone Number<span class="text-red-600">*</span></label>
          <input
            type="text"
            class="grow min-w-0 px-5 py-4 rounded-xl text-base lg:text-sm focus:outline-none border border-gray-300"
            name="phoneNumber"
            placeholder="Current Phone Number"
            aria-label="Current Phone Number"
            value="${phoneNumber}"
            disabled
            required
          />
          ${errors?.portInMdn && html`<p class="text-red-500 text-xs mt-1">${errors.portInMdn}</p>`}
        </fieldset>
        <fieldset class="flex flex-col">
          <label for="accountNumber" class="text-xs mb-1">Account Number<span class="text-red-600">*</span></label>
          <input
            type="text"
            class="grow min-w-0 px-5 py-4 rounded-xl text-base lg:text-sm focus:outline-none"
            name="accountNumber"
            value="${accountNumber}"
            onChange=${(e) => setAccountNumber(e.target.value)}
            placeholder="Account Number"
            aria-label="Account Number"
            required
          />
          ${errors?.ospAccountNumber && html`<p class="text-red-500 text-xs mt-1">${errors.ospAccountNumber}</p>`}
        </fieldset>
        <fieldset class="flex flex-col">
          <label for="transferPin" class="text-xs mb-1">Transfer PIN<span class="text-red-600">*</span></label>
          <input
            type="text"
            class="grow min-w-0 px-5 py-4 rounded-xl text-base lg:text-sm focus:outline-none"
            name="transferPin"
            value="${transferPin}"
            onChange=${(e) => setTransferPin(e.target.value)}
            placeholder="Transfer PIN"
            aria-label="Transfer PIN"
            required
          />
          ${errors?.ospAccountPassword && html`<p class="text-red-500 text-xs mt-1">${errors.ospAccountPassword}</p>`}
        </fieldset>
        <fieldset class="flex flex-col">
          <label for="zip" class="text-xs mb-1">Billing ZIP Code<span class="text-red-600">*</span></label>
          <input
            type="text"
            class="grow min-w-0 px-5 py-4 rounded-xl text-base lg:text-sm focus:outline-none"
            name="zip"
            value="${zipCode}"
            onChange=${(e) => setZipCode(e.target.value)}
            placeholder="Billing ZIP Code"
            aria-label="Billing ZIP Code"
            required
          />
          ${errors?.zipCode && html`<p class="text-red-500 text-xs mt-1">${errors.zipCode}</p>`}
        </fieldset>

        <div class="mt-2">
          <${Button} text="Update Transfer" className="w-full" loading=${isLoading} />
          ${
            showError &&
            html`<p class="text-red-500 text-sm mt-2">We couldn't transfer your number. Please try again.</p>`
          }
        </div>
      </form>
    </div>

    <div class="flex flex-col items-center mt-12">
      <h3 class="h5 mb-2.5">Do you want to start your activation over?</span>
      <${Button} 
        text="Reset Activation" 
        loading=${resetting} 
        onClick=${handleReset} 
        className="btn--sm" 
        arrow=${false}
        outline=${true}
      />
      ${resetError && html`<p class="text-red-500 text-xs mt-2">We couldn't reset your activation. Please try again.</p>`}
    </div>
  `;
};

export default StepNumberTransferIssue;
